/**
 * DowngradeFreeConfirmModal — Confirmation avant downgrade vers Free.
 *
 * Affiche en preview :
 *  - Abonnements wardrobe (Plus/Max) qui seront résiliés en fin de période
 *  - Nombre d'articles dressing au-delà du quota Free (20) qui seront masqués
 *  - Boost slots actifs qui resteront jusqu'à expiration (rassurant)
 *
 * v1.12.608 — V5.6 bundle v5.
 * Thème Mytenue : cream #F4ECDD / navy #1a1f36 / gold #D4AF37.
 */
import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import AppModal from './AppModal';
import { useI18n } from '../i18n';
import { fetchMyWardrobeSubscriptions } from '../services/StripeService';
import { TIERS } from '../services/PremiumService';

const COLORS = {
  cream:    '#F4ECDD',
  gold:     '#D4AF37',
  navy:     '#1a1f36',
  text:     '#1A1A1A',
  textLight:'#666',
  white:    '#FFF',
  danger:   '#C0392B',
  warning:  '#E67E22',
};

const FREE_WARDROBE_QUOTA = TIERS.free.wardrobe_items; // 20

function fmtDate(iso, lang) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(lang === 'en' ? 'en-US' : 'fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch {
    return '';
  }
}

export default function DowngradeFreeConfirmModal({
  visible,
  onClose,
  onConfirm,
  currentItemCount = 0,
}) {
  const { lang } = useI18n();
  const isFR = !lang || lang.startsWith('fr');

  const [loading, setLoading] = useState(false);
  const [wardrobeSubs, setWardrobeSubs] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setLoading(true);
    fetchMyWardrobeSubscriptions()
      .then(list => {
        if (cancelled) return;
        const active = (list || []).filter(s => s.status === 'active' && !s.cancel_at_period_end);
        setWardrobeSubs(active);
      })
      .catch(() => { if (!cancelled) setWardrobeSubs([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [visible]);

  if (!visible) return null;

  const itemsHidden = Math.max(0, currentItemCount - FREE_WARDROBE_QUOTA);
  const hasSubs = wardrobeSubs.length > 0;
  const hasHidden = itemsHidden > 0;

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppModal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={s.overlay}>
        <View style={s.card}>
          <Text style={s.title}>
            {isFR ? 'Passer en version gratuite ?' : 'Switch to Free?'}
          </Text>
          <Text style={s.subtitle}>
            {isFR
              ? 'Voici ce qui se passera si vous confirmez :'
              : 'Here is what will happen if you confirm:'}
          </Text>

          {loading ? (
            <ActivityIndicator color={COLORS.gold} style={{ marginVertical: 24 }} />
          ) : (
            <View style={s.body}>
              {/* Abonnements wardrobe à résilier */}
              {hasSubs && (
                <View style={s.bullet}>
                  <Text style={s.bulletIcon}>📦</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={s.bulletTitle}>
                      {isFR
                        ? `${wardrobeSubs.length} abonnement${wardrobeSubs.length > 1 ? 's' : ''} Dressing à résilier`
                        : `${wardrobeSubs.length} Wardrobe subscription${wardrobeSubs.length > 1 ? 's' : ''} to cancel`}
                    </Text>
                    {wardrobeSubs.map(sub => (
                      <Text key={sub.id} style={s.bulletDetail}>
                        • {sub.addon_id === 'subscription_wardrobe_max' ? 'Espace Max (+250)' : 'Espace Plus (+100)'}
                        {sub.current_period_end
                          ? ` — ${isFR ? 'actif jusqu\'au' : 'active until'} ${fmtDate(sub.current_period_end, lang)}`
                          : ''}
                      </Text>
                    ))}
                    <Text style={s.bulletNote}>
                      {isFR
                        ? 'Pas de remboursement — vous gardez l\'accès jusqu\'à la fin de la période payée.'
                        : 'No refund — access remains until the paid period ends.'}
                    </Text>
                  </View>
                </View>
              )}

              {/* Items qui seront masqués */}
              {hasHidden && (
                <View style={s.bullet}>
                  <Text style={s.bulletIcon}>🧥</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={s.bulletTitle}>
                      {isFR
                        ? `${itemsHidden} article${itemsHidden > 1 ? 's' : ''} sera${itemsHidden > 1 ? 'ont' : ''} masqué${itemsHidden > 1 ? 's' : ''}`
                        : `${itemsHidden} item${itemsHidden > 1 ? 's' : ''} will be hidden`}
                    </Text>
                    <Text style={s.bulletDetail}>
                      {isFR
                        ? `Le palier Free autorise ${FREE_WARDROBE_QUOTA} articles. Les ${itemsHidden} plus récents resteront sauvegardés mais cachés (jamais supprimés).`
                        : `Free tier allows ${FREE_WARDROBE_QUOTA} items. The ${itemsHidden} most recent will stay saved but hidden (never deleted).`}
                    </Text>
                    <Text style={s.bulletNote}>
                      {isFR
                        ? 'Vous les retrouverez en passant à Basic ou en achetant un Boost +50.'
                        : 'Recover them by upgrading to Basic or buying a +50 Boost.'}
                    </Text>
                  </View>
                </View>
              )}

              {/* Cas où rien à signaler */}
              {!hasSubs && !hasHidden && (
                <View style={s.bullet}>
                  <Text style={s.bulletIcon}>✅</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={s.bulletTitle}>
                      {isFR ? 'Aucun impact immédiat' : 'No immediate impact'}
                    </Text>
                    <Text style={s.bulletDetail}>
                      {isFR
                        ? 'Vous avez moins que la limite Free et aucun abonnement Dressing actif.'
                        : 'You are below the Free limit and have no active Wardrobe subscription.'}
                    </Text>
                  </View>
                </View>
              )}

              {/* Avantages perdus */}
              <View style={s.bullet}>
                <Text style={s.bulletIcon}>⚠️</Text>
                <View style={{ flex: 1 }}>
                  <Text style={s.bulletTitle}>
                    {isFR ? 'Avantages que vous perdez' : 'Benefits you lose'}
                  </Text>
                  <Text style={s.bulletDetail}>
                    {isFR
                      ? '• Looks IA limités à 5/jour\n• Scans IA limités à 5/jour\n• Try-Ons : 0/mois (sauf add-ons)\n• Crédits MyTenue réduits'
                      : '• AI Looks capped at 5/day\n• AI Scans capped at 5/day\n• Try-Ons: 0/month (except add-ons)\n• Fewer MyTenue Credits'}
                  </Text>
                </View>
              </View>
            </View>
          )}

          <View style={s.actions}>
            <TouchableOpacity
              style={s.cancelBtn}
              onPress={onClose}
              disabled={submitting}
              activeOpacity={0.7}
            >
              <Text style={s.cancelBtnText}>
                {isFR ? 'Garder mon abonnement' : 'Keep my subscription'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[s.confirmBtn, submitting && { opacity: 0.6 }]}
              onPress={handleConfirm}
              disabled={submitting || loading}
              activeOpacity={0.85}
            >
              {submitting ? (
                <ActivityIndicator color={COLORS.white} size="small" />
              ) : (
                <Text style={s.confirmBtnText}>
                  {isFR ? 'Confirmer le passage en Free' : 'Confirm switch to Free'}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </AppModal>
  );
}

const s = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  card: {
    backgroundColor: COLORS.cream,
    borderRadius: 18,
    padding: 22,
    width: '100%',
    maxWidth: 460,
    shadowColor: COLORS.navy,
    shadowOpacity: 0.3,
    shadowRadius: 30,
    shadowOffset: { width: 0, height: 10 },
    elevation: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: '800',
    color: COLORS.navy,
    textAlign: 'center',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 13,
    color: COLORS.textLight,
    textAlign: 'center',
    marginBottom: 16,
  },
  body: {
    gap: 12,
  },
  bullet: {
    flexDirection: 'row',
    gap: 10,
    backgroundColor: COLORS.white,
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: 'rgba(212, 175, 55, 0.3)',
  },
  bulletIcon: {
    fontSize: 22,
  },
  bulletTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: COLORS.navy,
    marginBottom: 4,
  },
  bulletDetail: {
    fontSize: 12,
    color: COLORS.text,
    lineHeight: 17,
  },
  bulletNote: {
    fontSize: 11,
    color: COLORS.textLight,
    fontStyle: 'italic',
    marginTop: 4,
    lineHeight: 15,
  },
  actions: {
    marginTop: 18,
    gap: 8,
  },
  cancelBtn: {
    paddingVertical: 12,
    borderRadius: 10,
    backgroundColor: COLORS.navy,
    alignItems: 'center',
  },
  cancelBtnText: {
    color: COLORS.gold,
    fontWeight: '800',
    fontSize: 14,
    letterSpacing: 0.3,
  },
  confirmBtn: {
    paddingVertical: 12,
    borderRadius: 10,
    backgroundColor: 'transparent',
    borderWidth: 1.5,
    borderColor: COLORS.danger,
    alignItems: 'center',
  },
  confirmBtnText: {
    color: COLORS.danger,
    fontWeight: '700',
    fontSize: 13,
  },
});
